import React, { useState, useEffect, useCallback } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from './ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from './ui/tooltip';
import {
  TrendingUp,
  TrendingDown,
  Download,
  History,
  Filter,
  RefreshCw,
  Loader2,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight
} from 'lucide-react';
import { useMediaQuery } from '../hooks/useMediaQuery';
import inventoryHistoryService from '../services/inventoryHistoryService';

const InventoryHistoryComponent = ({
  productId,
  productInfo = {},
  compact = false,
  maxHeight = 600,
  selectedClientId = 'global'
}) => {
  const isMobile = useMediaQuery('(max-width: 768px)');

  // State management
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [historyData, setHistoryData] = useState({
    history: [],
    pagination: {
      page: 1,
      page_size: 10,
      total_count: 0,
      has_next: false,
      has_prev: false
    },
    product: {}
  });

  // Pagination and filters
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [sortBy] = useState('created_at');
  const [sortOrder] = useState('desc');
  const [filters, setFilters] = useState({
    change_type: '',
    operator: '',
    client_id: selectedClientId && selectedClientId !== 'global' ? selectedClientId : ''
  });

  // Update client_id filter when selectedClientId changes
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      client_id: selectedClientId && selectedClientId !== 'global' ? selectedClientId : ''
    }));
    setPage(1); // Reset to first page when client changes
  }, [selectedClientId]);

  // Load history data
  const loadHistoryData = useCallback(async (currentPage = page, resetError = true) => {
    if (!productId) {
      setError('产品ID不能为空');
      return;
    }

    if (resetError) {
      setError('');
    }
    setLoading(true);

    try {
      // Extract location and current stock from productInfo if available
      const location = productInfo?.location || null;
      // For location-specific history, use the quantity at that location
      // productInfo may have remaining_quantity (global) or a location-specific quantity
      const currentStock = location ? (productInfo?.remaining_quantity || productInfo?.quantity || 0) : null;

      console.log('Loading inventory history with location:', location, 'currentStock:', currentStock, 'for product:', productId);

      const data = await inventoryHistoryService.getInventoryHistory(
        productId,
        currentPage,
        pageSize,
        sortBy,
        sortOrder,
        filters,
        location,
        currentStock
      );

      console.log('Inventory history data received:', data);
      console.log('History array length:', data.history?.length);
      console.log('Selected client ID:', selectedClientId);

      // For warehouse movements, we don't filter by client context at frontend level
      // The backend should handle client filtering if needed
      // TODO: Remove this client-specific filtering logic as it's not applicable to warehouse_movements
      setHistoryData(data);
    } catch (err) {
      console.error('Failed to load inventory history:', err);
      const errorMessage = err.response?.data?.error || '加载库存历史失败';
      setError(errorMessage);

      // Set empty data on error
      setHistoryData({
        history: [],
        pagination: {
          page: currentPage,
          page_size: pageSize,
          total_count: 0,
          has_next: false,
          has_prev: false
        },
        product: productInfo
      });
    } finally {
      setLoading(false);
    }
  }, [productId, page, pageSize, sortBy, sortOrder, filters, productInfo, selectedClientId]);

  // Initial load and reload on dependencies change
  useEffect(() => {
    if (productId) {
      loadHistoryData(1, true);
    }
  }, [productId, filters, loadHistoryData]);

  // Handle page change
  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
    loadHistoryData(newPage, false);
  }, [loadHistoryData]);

  // Handle filter change
  const handleFilterChange = useCallback((value) => {
    setFilters(prev => ({
      ...prev,
      change_type: value === 'all' ? '' : value
    }));
    setPage(1); // Reset to first page when filtering
  }, []);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    loadHistoryData(page, true);
  }, [loadHistoryData, page]);

  // Handle export
  const handleExport = useCallback(() => {
    if (!historyData.history.length) {
      setError('没有数据可以导出');
      return;
    }

    const success = inventoryHistoryService.exportHistoryToCSV(
      historyData.history,
      historyData.product || productInfo
    );

    if (!success) {
      setError('导出失败，请重试');
    }
  }, [historyData.history, historyData.product, productInfo]);

  // Clear error
  const handleClearError = useCallback(() => {
    setError('');
  }, []);

  // Render quantity change with appropriate styling
  const renderQuantityChange = useCallback((changeType, quantityChange) => {
    const formatted = inventoryHistoryService.formatQuantityChange(changeType, quantityChange);

    return (
      <div className="flex items-center gap-1">
        {formatted.sign === 'positive' ? (
          <TrendingUp className="h-4 w-4 text-success" />
        ) : (
          <TrendingDown className="h-4 w-4 text-destructive" />
        )}
        <span
          className={`text-sm font-bold ${
            formatted.sign === 'positive' ? 'text-success' : 'text-destructive'
          }`}
        >
          {formatted.text}
        </span>
      </div>
    );
  }, []);

  // Render change type chip
  const renderChangeTypeChip = useCallback((changeType) => {
    const label = inventoryHistoryService.getChangeTypeLabel(changeType);
    const color = inventoryHistoryService.getChangeTypeColor(changeType);

    const variantMap = {
      success: 'default',
      error: 'destructive',
      warning: 'outline',
      info: 'secondary',
      default: 'outline'
    };

    return (
      <Badge variant={variantMap[color] || 'outline'} className="min-w-[60px] justify-center">
        {label}
      </Badge>
    );
  }, []);

  // Empty state component
  const EmptyState = () => (
    <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
      <History className="h-16 w-16 mb-4 text-muted-foreground" />
      <h3 className="text-lg font-semibold mb-1">暂无库存历史记录</h3>
      <p className="text-sm text-muted-foreground">该产品还没有库存变化记录</p>
    </div>
  );

  // Pagination component
  const Pagination = () => {
    const totalPages = Math.ceil((historyData.pagination?.total_count || 0) / pageSize);

    return (
      <div className="flex items-center justify-center gap-1 p-4 border-t">
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8"
          onClick={() => handlePageChange(1)}
          disabled={loading || page === 1}
        >
          <ChevronsLeft className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8"
          onClick={() => handlePageChange(page - 1)}
          disabled={loading || page === 1}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="px-4 text-sm">
          {page} / {totalPages}
        </span>
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8"
          onClick={() => handlePageChange(page + 1)}
          disabled={loading || page >= totalPages}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8"
          onClick={() => handlePageChange(totalPages)}
          disabled={loading || page >= totalPages}
        >
          <ChevronsRight className="h-4 w-4" />
        </Button>
      </div>
    );
  };

  // Loading state
  if (loading && !historyData.history.length) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-primary mr-2" />
        <span className="text-sm">加载库存历史中...</span>
      </div>
    );
  }

  return (
    <div className="relative">
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription className="flex items-center justify-between">
            {error}
            <Button variant="ghost" size="sm" onClick={handleClearError}>
              关闭
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Header with controls */}
      <div className="mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 items-center">
          <div>
            <Select value={filters.change_type || 'all'} onValueChange={handleFilterChange}>
              <SelectTrigger className="w-full">
                <Filter className="h-4 w-4 mr-2 text-muted-foreground" />
                <SelectValue placeholder="筛选类型" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全部类型</SelectItem>
                {inventoryHistoryService.getChangeTypes().map((type) => (
                  <SelectItem key={type} value={type}>
                    {inventoryHistoryService.getChangeTypeLabel(type)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleRefresh}
                    disabled={loading}
                  >
                    <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>刷新数据</TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleExport}
                    disabled={loading || !historyData.history.length}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>导出CSV</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          {!compact && historyData.pagination?.total_count > 0 && (
            <div className="text-right">
              <span className="text-sm text-muted-foreground">
                共 {historyData.pagination.total_count} 条记录
              </span>
            </div>
          )}
        </div>
      </div>

      {/* History Table */}
      {historyData.history.length === 0 ? (
        <EmptyState />
      ) : (
        <Card className="border">
          <div className="overflow-auto" style={{ maxHeight: compact ? 400 : maxHeight }}>
            <table className="w-full text-sm">
              <thead className="bg-muted sticky top-0">
                <tr className="border-b">
                  <th className="text-left p-3 font-medium">日期时间</th>
                  <th className="text-left p-3 font-medium">类型</th>
                  {!isMobile && <th className="text-left p-3 font-medium">关联单据</th>}
                  <th className="text-center p-3 font-medium">库存变化</th>
                  <th className="text-center p-3 font-medium">变化前</th>
                  <th className="text-center p-3 font-medium">变化后</th>
                  {!isMobile && <th className="text-left p-3 font-medium">备注</th>}
                  {!isMobile && <th className="text-left p-3 font-medium">操作员</th>}
                </tr>
              </thead>
              <tbody>
                {historyData.history.map((item, index) => (
                  <tr
                    key={item._id || index}
                    className="border-b hover:bg-muted odd:bg-muted/50"
                  >
                    <td className="p-3">
                      <span className="text-sm">
                        {inventoryHistoryService.formatDate(item.created_at)}
                      </span>
                    </td>

                    <td className="p-3">
                      {renderChangeTypeChip(item.change_type)}
                    </td>

                    {!isMobile && (
                      <td className="p-3">
                        <span className="text-sm text-primary font-medium">
                          {item.source_reference || '-'}
                        </span>
                      </td>
                    )}

                    <td className="p-3 text-center">
                      {renderQuantityChange(item.change_type, item.quantity_change)}
                    </td>

                    <td className="p-3 text-center">
                      <span className="text-sm">
                        {item.stock_before !== null && item.stock_before !== undefined ? item.stock_before : '-'}
                      </span>
                    </td>

                    <td className="p-3 text-center">
                      <span className="text-sm font-bold text-primary">
                        {item.stock_after !== null && item.stock_after !== undefined ? item.stock_after : '-'}
                      </span>
                    </td>

                    {!isMobile && (
                      <td className="p-3">
                        {item.comments ? (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="text-sm text-muted-foreground max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap block cursor-help">
                                  {item.comments}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-w-[300px]">
                                <p className="whitespace-pre-wrap">{item.comments}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (
                          <span className="text-sm text-muted-foreground">-</span>
                        )}
                      </td>
                    )}

                    {!isMobile && (
                      <td className="p-3">
                        <span className="text-sm text-muted-foreground">
                          {item.operator_name || '-'}
                        </span>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {historyData.pagination?.total_count > pageSize && (
            <Pagination />
          )}
        </Card>
      )}

      {/* Loading overlay for data refresh */}
      {loading && historyData.history.length > 0 && (
        <div className="absolute inset-0 flex items-center justify-center bg-card/70 z-10">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      )}
    </div>
  );
};

export default InventoryHistoryComponent;
